/* eslint-disable react/destructuring-assignment */
import { Avatar, AvatarGroup, Hovercard, Icon, Link } from "@resource/atlas";
import {
  atlasCalendarCheck,
  atlasCalendarEvents,
  atlasClock,
} from "@resource/atlas/icons";
import { strings } from "@resource/common";
import clsx from "clsx";
import { DateTime } from "luxon";
import { ComponentPropsWithoutRef } from "react";
import { PersistableCalendarEvent } from "shared/guide-content/rich-blocks/calendar-utils/types";
import { labelForDuration } from "shared/guide-content/rich-blocks/request-availability";

import { getPlaceholderTitle } from "./utils";

function countUniqueDays(items: { start: string }[]): number {
  const uniqueDates = new Set(
    items.map((item) => DateTime.fromISO(item.start).toFormat("yyyy-MM-dd"))
  );

  return uniqueDates.size;
}

function formatEventDateRange({
  start,
  end,
  timezone,
}: {
  start: string;
  end: string;
  timezone: string;
}): string {
  const formattedStartDate = DateTime.fromISO(start)
    .setZone(timezone)
    .toFormat("EEE MMM d, h:mma");
  const formattedEndDate = DateTime.fromISO(end)
    .setZone(timezone)
    .toFormat("h:mma ZZZZ");

  return `${formattedStartDate} – ${formattedEndDate}`;
}

type BaseRequestAvailabilityInfoProps = ComponentPropsWithoutRef<"div"> & {
  className?: string;
  variant?: "default" | "large";
  title: string;
  description: string;
  duration: number;
  organization?: {
    name: string;
    avatarUrl: string;
  };
  candidate?: {
    name: string;
  };
};

type ViewRequestAvailabilityInfoProps = BaseRequestAvailabilityInfoProps & {
  isEditing?: false | undefined;
  isRecipient?: boolean;
};

type EditRequestAvailabilityInfoProps = BaseRequestAvailabilityInfoProps & {
  isEditing: true;
  onEdit(): void;
  timezone: string;
  senderSuggestions: PersistableCalendarEvent[];
  isRecipient?: boolean;
};

export type RequestAvailabilityInfoProps =
  | ViewRequestAvailabilityInfoProps
  | EditRequestAvailabilityInfoProps;

export function RequestAvailabilityInfo(props: RequestAvailabilityInfoProps) {
  const {
    variant = "default",
    title,
    description,
    duration,
    organization,
    candidate,
    isEditing,
    isRecipient,
    ...rest
  } = props;

  const validSuggestions = isEditing
    ? [...props.senderSuggestions].filter(
        (v): v is { start: string; end: string } => !!v.start && !!v.end
      )
    : [];
  const uniqueDays = validSuggestions ? countUniqueDays(validSuggestions) : 0;
  const placeholderTitle = getPlaceholderTitle();
  const titleWithFallback = title || placeholderTitle;

  return (
    <div {...rest} className={clsx("space-y-4", rest.className)}>
      <div
        className={clsx({
          "space-y-3": variant === "default",
          "space-y-4": variant === "large",
        })}
      >
        {organization && candidate && (
          <>
            <AvatarGroup size={variant === "default" ? "large" : "xl"}>
              <Avatar image={organization.avatarUrl} name={organization.name} />
              <Avatar name={candidate.name} />
            </AvatarGroup>
          </>
        )}
        <div className="space-y-2">
          <div className="space-y-2">
            {titleWithFallback && (
              <div className="flex flex-row gap-2">
                <h4
                  className={clsx({
                    "!text-h4": variant === "default",
                    "!text-h3": variant === "large",
                  })}
                >
                  {titleWithFallback}
                </h4>
              </div>
            )}
            {description && (
              <p
                className={clsx({
                  "text-body-md": variant === "default",
                  "text-body-lg": variant === "large",
                })}
              >
                {description}
              </p>
            )}
          </div>

          <div className="flex flex-row gap-4">
            {(!isRecipient || duration !== 0) && (
              <span className="text-body-md text-subtle flex gap-2">
                <Icon
                  content={atlasClock}
                  className="w-[1.25rem] h-[1.25rem]"
                />
                {labelForDuration(duration)}
              </span>
            )}
            {isEditing &&
              (validSuggestions.length ? (
                <div className="flex flex-row gap-2">
                  <Hovercard.Root>
                    <Hovercard.Anchor>
                      <div className="text-subtle flex gap-2">
                        <Icon
                          content={atlasCalendarEvents}
                          className="w-[1.25rem] h-[1.25rem]"
                        />
                        {strings.pluralize("time", validSuggestions.length)} on{" "}
                        {strings.pluralize("day", uniqueDays)}
                      </div>
                    </Hovercard.Anchor>
                    <Hovercard.Content>
                      <Hovercard.Heading className="!font-bold !mb-2 text-subtle uppercase">
                        Suggested times
                      </Hovercard.Heading>
                      <div className="space-y-3">
                        {validSuggestions
                          .sort((a, b) => (a.start > b.start ? 1 : -1))
                          .map((suggestedTime) => (
                            <div className="flex flex-row gap-2">
                              <Icon
                                content={atlasCalendarCheck}
                                className="w-[1.25rem] h-[1.25rem] text-subtle"
                              />
                              <span className="text-body-md">
                                {formatEventDateRange({
                                  ...suggestedTime,
                                  timezone: props.timezone,
                                })}
                              </span>
                            </div>
                          ))}
                      </div>
                    </Hovercard.Content>
                  </Hovercard.Root>
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <Link as="button" onClick={props.onEdit}>
                    Edit
                  </Link>
                </div>
              ) : (
                <div className="flex flex-row gap-2">
                  <div className="text-subtle flex gap-2">
                    <Icon
                      content={atlasCalendarEvents}
                      className="w-[1.25rem] h-[1.25rem]"
                    />
                    No suggested times
                  </div>
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <Link as="button" onClick={props.onEdit}>
                    Edit
                  </Link>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
